import { hasOpenedModifyPasswordAtom } from '@/App/Layout/atoms'
import { useInitAdminFeatures } from '@/App/hooks'
import { ChangePassword } from '@/features/teacherManagement/pages/components/ChangePassword'
import {
  useIsCurrentStudentArchived,
  useCurrentReflection,
  useCurrentSchoolId,
} from '@/hooks'
import { reflectionStorage, store, xRbacTokenStorage } from '@/store'
import { Button, Modal, State, Toast } from '@seiue/ui'
import { useAtom } from 'jotai'
import { permApi, reflectionApi } from 'packages/sdks-next/growp'
import { ReactNode, useEffect, useState } from 'react'
import { useHistory as useOriginHistory } from 'react-router-dom'
import styled from 'styled-components'

import { AccountManageContent } from './AccountManageContent'
import { LayoutHeaderRight } from './LayoutHeaderRight'
import { MyNews } from './MyNews'
import { UserContent } from './UserContent'
import { UserDetailModal } from './UserDetailModal'
import { GatewayLayoutHeader } from './components/GatewayLayoutHeader'
import { GatewayContext } from './context'
import { ModuleName } from './types'

/**
 * 正式用户 layout
 *
 * @returns 组件
 */
export const FormalUserLayout: React.FC = () => {
  const { data: perms, loading } = permApi.useQueryMePerms()
  const [hasOpenedModifyPassword, setHasOpenedModifyPassword] = useAtom(
    hasOpenedModifyPasswordAtom
  )
  const [hasOpenPasswordModal, setHasOpenPasswordModal] =
    useState<boolean>(false)

  const [module, setModule] = useState<ModuleName>('index')

  const schoolId = useCurrentSchoolId()
  const historyOrigin = useOriginHistory()
  const reflection = useCurrentReflection()
  const isStudentArchived = useIsCurrentStudentArchived()
  const { api: updateReflection } = reflectionApi.useUpdateSelfPassInlogin()

  useEffect(() => {
    if (!perms) return
    const { passwordChangeNeeded } = perms || {}
    if (
      passwordChangeNeeded &&
      !hasOpenedModifyPassword &&
      !hasOpenPasswordModal
    ) {
      modifyPassword(true)
    }
  }, [perms, hasOpenedModifyPassword])

  const modifyPassword = (initPassword?: boolean) => {
    setHasOpenPasswordModal(true)
    const { hide } = Modal.open({
      title: '修改密码',
      children: (
        <ChangePassword
          tips={initPassword}
          hide={() => hide()}
          onOk={async (password: string, repeatPassword: string) => {
            if (!reflection) return
            await updateReflection({
              id: reflection.rid,
              payload: {
                password,
                repeatPassword,
              },
            })
            Toast.success('密码修改成功, 请重新登录')
            hide()
            store.set(reflectionStorage, undefined)
            store.set(xRbacTokenStorage, undefined)
            historyOrigin.push(`/login/${reflection.schoolId || schoolId}`)
          }}
        />
      ),
      onCancel: () => {
        hide()
        setHasOpenedModifyPassword(true)
      },
      footer: null,
      bodyStyle: {
        padding: '0 0',
      },
    })
  }

  useInitAdminFeatures()

  const contentMap: {
    [K in ModuleName]: ReactNode
  } = {
    index: <UserContent />,
    'account-manage': <AccountManageContent />,
    news: <MyNews />,
  }

  const content = contentMap[module]

  if (loading || !reflection) return <State.Loading />
  return (
    <GatewayContext.Provider
      value={{
        setModule,
        module,
        perms,
      }}
    >
      <Layout>
        <GatewayLayoutHeader headerRight={<LayoutHeaderRight />} />
        {isStudentArchived ? (
          <UserDetailModal
            rid={reflection.rid}
            role={reflection.role || ''}
            isArchived={true}
          />
        ) : (
          <ContentScrollArea>{content}</ContentScrollArea>
        )}
      </Layout>
    </GatewayContext.Provider>
  )
}

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 100vh; /* 华为等浏览器：父链不完整时仍能限制高度 */
  width: fit-content;
  min-width: 100%;
  background-color: ${(props) => props.theme.background._3};
  overflow: hidden;
`

/** 内容滚动区：flex 子项需 min-height:0 才能在部分浏览器中正确出现滚动 */
const ContentScrollArea = styled.div`
  flex: 1 1 0;
  min-height: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
`
